import { useMemo, useState } from "react";

import type { LegacyMatch } from "../api/legacyTypes";
import { usePlayerSummaries } from "../hooks/usePlayerSummaries";
import { useTournamentResults } from "../hooks/useTournamentResults";
import { dateTimeSortValue } from "../utils/dateTime";
import { LocalDateTime } from "./LocalDateTime";
import { ResultParticipants } from "./ResultParticipants";

function selectQuickResults(completed: LegacyMatch[], isNew: (match: LegacyMatch) => boolean) {
  const unseen = completed.filter(isNew);
  const seen = completed.filter((match) => !isNew(match)).slice(-5).reverse();
  return [...unseen, ...seen.filter((match) => !unseen.includes(match))];
}

export function QuickResults({ tournamentId, gameId, matches }: { tournamentId: string; gameId: string; matches: LegacyMatch[] }) {
  const [open, setOpen] = useState(false);
  const [pinnedResultKeys, setPinnedResultKeys] = useState<string[]>([]);
  const [revealedResultKeys, setRevealedResultKeys] = useState<Set<string>>(() => new Set());
  const resultState = useTournamentResults(tournamentId, matches);
  const summaries = usePlayerSummaries(gameId, matches).data ?? {};
  const completed = useMemo(() => matches.filter((match) => match.status === "completed").sort((a, b) => dateTimeSortValue(a.startsAt) - dateTimeSortValue(b.startsAt)), [matches]);
  const visibleResults = useMemo(() => {
    const byKey = new Map(completed.map((match) => [match.sourceKey, match]));
    return pinnedResultKeys.map((key) => byKey.get(key)).filter((match): match is LegacyMatch => Boolean(match));
  }, [completed, pinnedResultKeys]);
  const togglePanel = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setPinnedResultKeys(selectQuickResults(completed, resultState.isNew).map((match) => match.sourceKey));
    setRevealedResultKeys(new Set());
    setOpen(true);
    resultState.initialize();
  };
  const reveal = (match: LegacyMatch) => {
    setRevealedResultKeys((current) => new Set(current).add(match.sourceKey));
    resultState.reveal(match);
  };
  const revealAll = () => {
    setRevealedResultKeys((current) => {
      const next = new Set(current);
      visibleResults.filter(resultState.hidden).forEach((match) => next.add(match.sourceKey));
      return next;
    });
    resultState.revealAll();
  };
  return <div className="quick-results">
    <button type="button" className="button-secondary" aria-expanded={open} onClick={togglePanel}>Quick results{resultState.newCount > 0 ? ` · ${resultState.newCount} new` : ""}</button>
    {open && <section className="quick-results-panel">
      <header><div><strong>Latest completed results</strong><small>Unseen oldest-first, then the 5 latest seen. Results stay in place while this panel is open.</small></div>{resultState.antiSpoiler && visibleResults.some(resultState.hidden) && <button type="button" onClick={revealAll}>Reveal all</button>}</header>
      {visibleResults.length === 0 && <p>No completed results yet.</p>}
      <div className="quick-result-grid">{visibleResults.map((match) => {
        const hidden = resultState.hidden(match);
        const revealed = revealedResultKeys.has(match.sourceKey);
        return <article key={match.sourceKey} data-result-key={match.sourceKey} className={[hidden ? "result-hidden" : "", revealed ? "result-revealed" : ""].filter(Boolean).join(" ")}>
          <small>{[match.group, match.round, match.sourceKey].filter(Boolean).join(" · ")}{revealed && <span className="quick-result-revealed-label">Revealed</span>}</small>
          <ResultParticipants gameId={gameId} match={match} summaries={summaries} />
          <LocalDateTime value={match.startsAt} status={match.status} />
          {hidden && <button type="button" onClick={() => reveal(match)}>Reveal</button>}
        </article>;
      })}</div>
    </section>}
  </div>;
}
